import { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = (process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_KEY)
  ? createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY)
  : null;

const INDUSTRY_LIST = [
  "Cold Storage & Food Processing","Logistics, Warehousing & Trucking","Light Manufacturing",
  "Pulp, Paper & Printing","Metals & Foundries","Cement, Glass & Building Materials",
  "Waste & Recycling","Textile & Garment","Defense, Aerospace & Ordinance",
  "Chemical & Petrochemical","Electronics, Semiconductor & Robotics Mfg","EV, Automotive & Fleet",
  "Port & Intermodal","Energy & Utilities","Life Sciences & Medical","Film, Media & Studios",
  "Airport & Ground Support","Cannabis & Controlled Agriculture","University & Research Campus",
  "Data Centers","Mixed / Other Industrial",
];

const METROS = ["All metros","Los Angeles","Bay Area","Dallas–Fort Worth","Chicago","Denver","Houston"];
const STATUSES = ["Unworked","In Research","Active","Passed"];
const PBUCKETS = ["0–500kW","500kW–2MW","2–10 MW","10+ MW"];
const PSOURCES = ["Auto-inferred","Utility confirmed","Permit data","Site visit"];

const C = {
  bg:"#E4E8DB", card:"#F5F7EC", border:"#C4C9B3", text:"#1C1F1A",
  textSec:"#6B6A5A", textMute:"#8A8A7A",
  green:"#2F8A1C", greenBg:"#E8F0D8",
  blue:"#6FA5D4", blueBg:"#EAF2FB",
  amber:"#D99600", amberBg:"#FEF3C7",
  red:"#D93814", redBg:"#F2DFCF",
};

const FLAG = {
  green:{bg:C.greenBg,color:C.green},
  amber:{bg:C.amberBg,color:C.amber},
  red:{bg:C.redBg,color:C.red},
};
const PBC = {
  "10+ MW":{bg:C.amberBg,color:C.amber},
  "2–10 MW":{bg:C.blueBg,color:C.blue},
  "500kW–2MW":{bg:C.greenBg,color:C.green},
  "0–500kW":{bg:C.card,color:C.textMute},
};
const STC = {
  "Active":{bg:C.greenBg,color:C.green},
  "In Research":{bg:C.blueBg,color:C.blue},
  "Passed":{bg:C.redBg,color:C.red},
  "Unworked":{bg:C.card,color:C.textMute},
};

function powerFlag(z){const pb=z.power_bucket;if(pb==="10+ MW")return"green";if(pb==="2–10 MW"&&z.power_conf==="High")return"green";if(pb==="2–10 MW")return"amber";if(pb==="500kW–2MW")return"amber";return"red";}
function subFlag(d){return d<0.75?"green":d<1.5?"amber":"red";}
function fiberFlag(d){return d<8?"green":d<16?"amber":"red";}
function demandFlag(v){return v>=88?"green":v>=78?"amber":"red";}
function pScore(z){const b={"10+ MW":100,"2–10 MW":80,"500kW–2MW":55,"0–500kW":30}[z.power_bucket]||50;const c={"High":5,"Medium":0,"Low":-5}[z.power_conf]||0;return Math.min(100,b+c);}
function sScore(d){return d<0.5?95:d<1?82:d<1.5?68:d<2?55:40;}
function fScore(d){return d<5?95:d<10?78:d<15?62:d<20?48:35;}

const mono="'JetBrains Mono',monospace";
const sel={background:C.card,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",color:C.text,fontSize:12,fontFamily:mono,outline:"none"};

// ─── Responsive hook ─────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

// Breakpoints
// wide  : ≥960  — all 7 columns
// mid   : 640–959 — 5 columns (drop Signals, abbreviate sub)
// narrow: <640  — card layout

function getLayout(width) {
  if (width >= 960) return "wide";
  if (width >= 640) return "mid";
  return "narrow";
}

// Grid templates per layout
const GRIDS = {
  wide: "26px minmax(0,2fr) minmax(0,1.5fr) 80px 130px 180px 80px",
  mid:  "20px minmax(0,1fr) 80px 100px 80px",
};

const HEADERS = {
  wide: ["#","Zone","Industry","Power","Nearest sub.","Signals","Status"],
  mid:  ["#","Zone","Power","Nearest sub.","Status"],
};

function Dot({flag}){return<span style={{display:"inline-block",width:7,height:7,borderRadius:"50%",background:FLAG[flag].color,flexShrink:0}}/>;}

function FlagBadge({label,flag}){const s=FLAG[flag];return<span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10,padding:"2px 5px",borderRadius:5,background:s.bg,color:s.color,whiteSpace:"nowrap",fontFamily:mono}}><Dot flag={flag}/>{label}</span>;}

function Tag({label,dominant}){return<span style={{fontSize:10,padding:"1px 7px",borderRadius:5,border:`0.5px solid ${dominant?C.textSec:C.border}`,background:C.card,color:dominant?C.text:C.textSec,fontWeight:dominant?600:400,whiteSpace:"nowrap",fontFamily:mono}}>{label}</span>;}

export default function Scout(){
  const[zones,setZones]=useState([]);
  const[loading,setLoading]=useState(true);
  const[error,setError]=useState(null);
  const[saving,setSaving]=useState(null);
  const[metro,setMetro]=useState("All metros");
  const[industry,setIndustry]=useState("All industries");
  const[sort,setSort]=useState("power");
  const[expanded,setExpanded]=useState(null);
  const[editForm,setEditForm]=useState(null);
  const width = useWindowWidth();
  const layout = getLayout(width);

  useEffect(()=>{
    if(!supabase){setError("Supabase not configured");setLoading(false);return;}
    supabase.from("zones").select("*").order("created_at",{ascending:true}).then(({data,error})=>{
      if(error)setError(error.message);else setZones(data||[]);
      setLoading(false);
    });
  },[]);

  async function saveZone(id,updates){
    if(!supabase)return;
    setSaving(id);
    const{error}=await supabase.from("zones").update({...updates,updated_at:new Date().toISOString()}).eq("id",id);
    if(error){alert("Save failed: "+error.message);setSaving(null);return;}
    setZones(prev=>prev.map(z=>z.id===id?{...z,...updates}:z));
    setSaving(null);setEditForm(null);
  }

  const usedIndustries=["All industries",...INDUSTRY_LIST.filter(ind=>zones.some(z=>(z.industries||[]).includes(ind)))];

  let filtered=metro==="All metros"?[...zones]:zones.filter(z=>z.metro===metro);
  if(industry!=="All industries")filtered=filtered.filter(z=>(z.industries||[]).includes(industry));

  const po={"10+ MW":4,"2–10 MW":3,"500kW–2MW":2,"0–500kW":1};
  if(sort==="power")filtered.sort((a,b)=>(po[b.power_bucket]||0)-(po[a.power_bucket]||0)||a.sub_dist-b.sub_dist);
  else if(sort==="sub")filtered.sort((a,b)=>a.sub_dist-b.sub_dist);
  else if(sort==="ixp")filtered.sort((a,b)=>a.ixp_dist-b.ixp_dist);
  else if(sort==="status"){const so={"Active":0,"In Research":1,"Unworked":2,"Passed":3};filtered.sort((a,b)=>(so[a.status]||2)-(so[b.status]||2));}

  if(loading)return<div style={{padding:40,textAlign:"center",color:C.textMute,fontFamily:mono,fontSize:12}}>Loading zones…</div>;
  if(error)return<div style={{padding:40,textAlign:"center",color:C.red,fontFamily:mono,fontSize:12}}>Error: {error}</div>;

  const grid = GRIDS[layout] || GRIDS.wide;
  const headers = HEADERS[layout] || HEADERS.wide;

  return(
    <div style={{fontFamily:mono}}>
      <div style={{fontSize:11,fontFamily:mono,textTransform:"uppercase",letterSpacing:1.5,color:"#A0B0BA",marginBottom:14}}>Scout — Industrial Zone Intelligence</div>

      {/* Filters */}
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
        <select value={metro} onChange={e=>{setMetro(e.target.value);setExpanded(null);}} style={sel}>
          {METROS.map(m=><option key={m} value={m}>{m}{m!=="All metros"?` (${zones.filter(z=>z.metro===m).length})`:` (${zones.length})`}</option>)}
        </select>
        <select value={industry} onChange={e=>{setIndustry(e.target.value);setExpanded(null);}} style={{...sel,maxWidth:layout==="narrow"?"100%":240}}>
          {usedIndustries.map(ind=><option key={ind} value={ind}>{ind}</option>)}
        </select>
        <div style={{marginLeft:layout==="narrow"?0:"auto",display:"flex",gap:7,alignItems:"center"}}>
          <span style={{fontSize:10,color:C.textMute}}>sort by</span>
          <select value={sort} onChange={e=>setSort(e.target.value)} style={sel}>
            <option value="power">Power bucket</option>
            <option value="sub">Substation dist.</option>
            <option value="ixp">IXP dist.</option>
            <option value="status">Status</option>
          </select>
        </div>
      </div>

      <div style={{fontSize:11,color:C.textMute,marginBottom:8}}>{filtered.length} zone{filtered.length!==1?"s":""}</div>

      {/* ─── TABLE LAYOUT (wide + mid) ─────────────────────── */}
      {layout !== "narrow" && (
        <>
          <div style={{display:"grid",gridTemplateColumns:grid,gap:8,padding:"5px 10px",borderBottom:`1px solid ${C.border}`}}>
            {headers.map(h=><span key={h} style={{fontSize:10,color:C.textMute,textTransform:"uppercase",letterSpacing:0.7,whiteSpace:"nowrap"}}>{h}</span>)}
          </div>

          {filtered.map((z,i)=>{
            const isExp=expanded===z.id;
            const isEditing=editForm?.id===z.id;
            const tags=z.industries||[];
            const pf=powerFlag(z),sf=subFlag(z.sub_dist),ff=fiberFlag(z.ixp_dist),df=demandFlag(z.demand);
            const pbS=PBC[z.power_bucket]||PBC["0–500kW"];
            const stS=STC[z.status]||STC["Unworked"];
            const bdVals=[Math.round(pScore(z)),sScore(z.sub_dist),fScore(z.ixp_dist),z.demand];
            const bdFlags=[pf,sf,ff,df];
            const bdLabels=[["Power likelihood","35%"],["Substation proximity","25%"],["Fiber proximity","20%"],["Demand proximity","20%"]];
            const ps=z.power_source||"Auto-inferred";
            const notes=z.notes||"";
            const hasEdits=z.updated_at!==z.created_at;

            return(
              <div key={z.id} style={{borderBottom:`1px solid ${C.border}`}}>
                <div
                  onClick={()=>setExpanded(isExp?null:z.id)}
                  onMouseEnter={e=>{if(!isExp)e.currentTarget.style.background=C.greenBg;}}
                  onMouseLeave={e=>{if(!isExp)e.currentTarget.style.background="transparent";}}
                  style={{display:"grid",gridTemplateColumns:grid,gap:8,padding:"9px 10px",cursor:"pointer",alignItems:"start",background:isExp?C.greenBg:"transparent"}}
                >
                  <span style={{fontSize:11,color:C.textMute,paddingTop:2}}>{i+1}</span>

                  {/* Zone */}
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{z.name}</div>
                    <div style={{fontSize:10,color:C.textMute,marginTop:1}}>{z.metro}</div>
                  </div>

                  {/* Industry — wide only */}
                  {layout === "wide" && (
                    <div style={{paddingTop:2,minWidth:0}}>
                      {tags[0] && <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:10,padding:"1px 7px",borderRadius:5,border:`0.5px solid ${C.textSec}`,background:C.card,color:C.text,fontWeight:600,fontFamily:mono,display:"inline-block",maxWidth:"100%",boxSizing:"border-box"}}>{tags[0]}</div>}
                      {tags.length > 1 && <div style={{fontSize:9,color:C.textMute,marginTop:3}}>+{tags.length-1} more</div>}
                    </div>
                  )}

                  {/* Power */}
                  <span style={{fontSize:10,padding:"2px 6px",borderRadius:5,background:pbS.bg,color:pbS.color,whiteSpace:"nowrap",marginTop:2,display:"inline-block"}}>{z.power_bucket}</span>

                  {/* Nearest sub */}
                  <span style={{fontSize:11,color:C.textSec,whiteSpace:"nowrap",paddingTop:2,overflow:"hidden",textOverflow:"ellipsis"}}>
                    {(z.sub_name||"").replace(/ Sub(station)?/,"")} <span style={{color:C.textMute}}>{z.sub_dist}mi</span>
                  </span>

                  {/* Signals — wide only */}
                  {layout === "wide" && (
                    <div style={{display:"flex",gap:4,alignItems:"center",paddingTop:2,flexWrap:"wrap"}}>
                      <FlagBadge label="Power" flag={pf}/>
                      <FlagBadge label="Grid" flag={sf}/>
                      <FlagBadge label="Fiber" flag={ff}/>
                      <FlagBadge label="Demand" flag={df}/>
                    </div>
                  )}

                  {/* Status */}
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:stS.bg,color:stS.color,whiteSpace:"nowrap",textAlign:"center",marginTop:2,display:"inline-block"}}>{z.status}</span>
                </div>

                {isExp&&(
                  <ExpandedRow
                    z={z} tags={tags} pf={pf} sf={sf} ff={ff} df={df}
                    bdVals={bdVals} bdFlags={bdFlags} bdLabels={bdLabels}
                    ps={ps} notes={notes} hasEdits={hasEdits}
                    isEditing={isEditing} editForm={editForm} setEditForm={setEditForm}
                    saving={saving} saveZone={saveZone} layout={layout}
                  />
                )}
              </div>
            );
          })}
        </>
      )}

      {/* ─── CARD LAYOUT (narrow) ──────────────────────────── */}
      {layout === "narrow" && filtered.map((z,i)=>{
        const isExp=expanded===z.id;
        const isEditing=editForm?.id===z.id;
        const tags=z.industries||[];
        const pf=powerFlag(z),sf=subFlag(z.sub_dist),ff=fiberFlag(z.ixp_dist),df=demandFlag(z.demand);
        const pbS=PBC[z.power_bucket]||PBC["0–500kW"];
        const stS=STC[z.status]||STC["Unworked"];
        const bdVals=[Math.round(pScore(z)),sScore(z.sub_dist),fScore(z.ixp_dist),z.demand];
        const bdFlags=[pf,sf,ff,df];
        const bdLabels=[["Power likelihood","35%"],["Substation proximity","25%"],["Fiber proximity","20%"],["Demand proximity","20%"]];
        const ps=z.power_source||"Auto-inferred";
        const notes=z.notes||"";
        const hasEdits=z.updated_at!==z.created_at;

        return(
          <div key={z.id} style={{borderBottom:`1px solid ${C.border}`}}>
            <div
              onClick={()=>setExpanded(isExp?null:z.id)}
              style={{padding:"10px 10px",cursor:"pointer",background:isExp?C.greenBg:"transparent"}}
            >
              {/* Row 1: index + name + status */}
              <div style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:5}}>
                <span style={{fontSize:10,color:C.textMute,paddingTop:2,flexShrink:0}}>{i+1}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{z.name}</div>
                  <div style={{fontSize:10,color:C.textMute,marginTop:1}}>{z.metro}</div>
                </div>
                <span style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:stS.bg,color:stS.color,whiteSpace:"nowrap",flexShrink:0}}>{z.status}</span>
              </div>

              {/* Row 2: power + sub + signals */}
              <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap",paddingLeft:18}}>
                <span style={{fontSize:10,padding:"2px 6px",borderRadius:5,background:pbS.bg,color:pbS.color,whiteSpace:"nowrap"}}>{z.power_bucket}</span>
                <span style={{fontSize:10,color:C.textMute,whiteSpace:"nowrap"}}>{(z.sub_name||"").replace(/ Sub(station)?/,"")} {z.sub_dist}mi</span>
                <FlagBadge label="P" flag={pf}/>
                <FlagBadge label="G" flag={sf}/>
                <FlagBadge label="F" flag={ff}/>
                <FlagBadge label="D" flag={df}/>
              </div>
            </div>

            {isExp&&(
              <ExpandedRow
                z={z} tags={tags} pf={pf} sf={sf} ff={ff} df={df}
                bdVals={bdVals} bdFlags={bdFlags} bdLabels={bdLabels}
                ps={ps} notes={notes} hasEdits={hasEdits}
                isEditing={isEditing} editForm={editForm} setEditForm={setEditForm}
                saving={saving} saveZone={saveZone} layout={layout}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Shared expanded detail panel ────────────────────────────
function ExpandedRow({z,tags,pf,sf,ff,df,bdVals,bdFlags,bdLabels,ps,notes,hasEdits,isEditing,editForm,setEditForm,saving,saveZone,layout}){
  const expandCols = layout === "narrow" ? "1fr" : "1fr 1fr";
  return(
    <div style={{padding:"14px 12px 16px",background:C.card,borderTop:`1px solid ${C.border}`}}>
      <div style={{display:"grid",gridTemplateColumns:expandCols,gap:20}}>
        <div>
          <div style={{fontSize:9,fontWeight:700,color:C.textMute,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8,paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>Auto-generated</div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,color:C.textMute,marginBottom:5}}>Industries <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(bold = dominant)</span></div>
            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>{tags.map((t,ti)=><Tag key={t} label={t} dominant={ti===0}/>)}</div>
          </div>
          <Row label="Power bucket (auto)" value={`${z.power_bucket} · ${z.power_conf} confidence`}/>
          <div style={{marginBottom:7}}>
            <div style={{fontSize:10,color:C.textMute,marginBottom:1}}>Nearest substation</div>
            <div style={{fontSize:12,color:C.text}}>{z.sub_name}</div>
            <div style={{fontSize:10,color:C.textMute}}>{z.sub_dist} mi · Built {z.sub_built} · {z.sub_voltage} · {z.sub_op}</div>
          </div>
          <Row label="Nearest IXP" value={`${z.ixp_name} · ${z.ixp_dist} mi`}/>
          <Row label="Demand score" value={`${z.demand} / 100`}/>
        </div>
        <div>
          <div style={{fontSize:9,fontWeight:700,color:C.textMute,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8,paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>Breakdown</div>
          {bdLabels.map(([l,w],idx)=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
              <Dot flag={bdFlags[idx]}/>
              <span style={{fontSize:10,color:C.textSec,flex:1}}>{l} <span style={{color:C.textMute}}>({w})</span></span>
              <div style={{width:60,height:3,background:C.border,borderRadius:2,flexShrink:0}}>
                <div style={{width:`${bdVals[idx]}%`,height:"100%",background:FLAG[bdFlags[idx]].color,borderRadius:2}}/>
              </div>
              <span style={{fontSize:11,color:C.text,width:22,textAlign:"right",flexShrink:0}}>{bdVals[idx]}</span>
            </div>
          ))}
          {(z.linked_sites||[]).length>0&&(
            <div style={{marginTop:14}}>
              <div style={{fontSize:9,fontWeight:700,color:C.textMute,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6,paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>Linked sites</div>
              {(z.linked_sites||[]).map(s=><div key={s} style={{fontSize:11,color:C.blue,padding:"2px 0"}}>{s}</div>)}
            </div>
          )}
          <div style={{marginTop:14}}>
            <div style={{fontSize:9,fontWeight:700,color:C.textMute,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6,paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>
              Your overrides {hasEdits&&<span style={{color:C.green,textTransform:"none",letterSpacing:0,fontWeight:400}}>· edited</span>}
            </div>
            <div style={{fontSize:11,color:C.textSec,marginBottom:6}}>Status: <strong style={{color:C.text}}>{z.status}</strong> &nbsp;·&nbsp; Source: <strong style={{color:C.text}}>{ps}</strong></div>
            {notes&&<div style={{fontSize:11,color:C.textSec,background:C.bg,padding:"6px 8px",borderRadius:5,marginBottom:8,border:`1px solid ${C.border}`}}>{notes}</div>}
            {!isEditing&&<button onClick={e=>{e.stopPropagation();setEditForm({id:z.id,status:z.status||"Unworked",power_bucket:z.power_bucket,power_source:ps,notes,industries:[...tags]});}} style={{fontSize:11,padding:"4px 10px",border:`1px solid ${C.border}`,borderRadius:5,background:C.card,color:C.textSec,cursor:"pointer"}}>Edit overrides</button>}
          </div>
        </div>
      </div>

      {isEditing&&editForm&&(
        <div onClick={e=>e.stopPropagation()} style={{background:C.bg,padding:12,borderRadius:7,border:`1px solid ${C.border}`,marginTop:14}}>
          <div style={{fontSize:11,fontWeight:600,color:C.textSec,marginBottom:12}}>Edit overrides — {z.name}</div>
          <div style={{display:"grid",gridTemplateColumns:layout==="narrow"?"1fr":"1fr 1fr 1fr",gap:10,marginBottom:12}}>
            {[["Status","status",STATUSES],["Power bucket","power_bucket",PBUCKETS],["Power source","power_source",PSOURCES]].map(([label,field,opts])=>(
              <div key={field}>
                <label style={{fontSize:10,color:C.textMute,display:"block",marginBottom:3,textTransform:"uppercase"}}>{label}</label>
                <select value={editForm[field]} onChange={e=>setEditForm(f=>({...f,[field]:e.target.value}))} style={{width:"100%",fontSize:11,padding:"4px 6px",borderRadius:5,border:`1px solid ${C.border}`,background:C.card,color:C.text,outline:"none"}}>
                  {opts.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:10,color:C.textMute,display:"block",marginBottom:5,textTransform:"uppercase"}}>Industries <span style={{fontWeight:400,textTransform:"none"}}>(first = dominant)</span></label>
            <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:6}}>
              {editForm.industries.map((t,idx)=>(
                <span key={t} style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10,padding:"2px 8px",borderRadius:5,border:`1px solid ${idx===0?C.textSec:C.border}`,background:C.card,color:idx===0?C.text:C.textSec}}>
                  {t}<button onClick={()=>setEditForm(f=>({...f,industries:f.industries.filter((_,i)=>i!==idx)}))} style={{fontSize:13,lineHeight:1,background:"none",border:"none",cursor:"pointer",color:C.textMute,padding:"0 0 0 2px"}}>×</button>
                </span>
              ))}
            </div>
            <select defaultValue="" onChange={e=>{const val=e.target.value;if(!val||editForm.industries.includes(val))return;setEditForm(f=>({...f,industries:[...f.industries,val]}));e.target.value="";}} style={{width:"100%",fontSize:11,padding:"4px 8px",borderRadius:5,border:`1px solid ${C.border}`,background:C.card,color:C.text,outline:"none"}}>
              <option value="">Add industry from list…</option>
              {INDUSTRY_LIST.filter(ind=>!editForm.industries.includes(ind)).map(ind=><option key={ind} value={ind}>{ind}</option>)}
            </select>
          </div>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:10,color:C.textMute,display:"block",marginBottom:3,textTransform:"uppercase"}}>Notes</label>
            <textarea value={editForm.notes} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} style={{width:"100%",fontSize:11,padding:"6px 8px",borderRadius:5,border:`1px solid ${C.border}`,background:C.card,color:C.text,resize:"vertical",minHeight:44,fontFamily:mono,boxSizing:"border-box",outline:"none"}} placeholder="e.g. SCE confirmed 4MW available on Bandini feeder…"/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button disabled={saving===z.id} onClick={()=>saveZone(z.id,{status:editForm.status,power_bucket:editForm.power_bucket,power_source:editForm.power_source,notes:editForm.notes,industries:editForm.industries})} style={{fontSize:11,padding:"5px 14px",border:"none",borderRadius:5,background:C.green,color:C.card,cursor:"pointer",fontWeight:600,opacity:saving===z.id?0.6:1}}>{saving===z.id?"Saving…":"Save"}</button>
            <button onClick={()=>setEditForm(null)} style={{fontSize:11,padding:"5px 12px",border:`1px solid ${C.border}`,borderRadius:5,background:"none",color:C.textSec,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({label,value}){
  return(
    <div style={{marginBottom:7}}>
      <div style={{fontSize:10,color:"#8A8A7A",marginBottom:1}}>{label}</div>
      <div style={{fontSize:12,color:"#1C1F1A"}}>{value}</div>
    </div>
  );
}
